const CACHE_NAME = "atlas-image-cache";
import Bugsnag from "@bugsnag/js";
/**
 * Fetches and caches an image by URL. Returns the cached response if available.
 *
 * @param url - The URL of the image to fetch and cache.
 * @returns The response object from the cache or network.
 * @throws Error if the network response is not OK.
 */
async function cacheImage(url: string): Promise<Response | undefined> {
  if (!("caches" in window)) return;

  try {
    const cache = await caches.open(CACHE_NAME);

    const cached = await cache.match(url);
    if (cached) return cached;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
    }

    cache.put(url, response.clone()).catch(err => {
      reportToBugsnag(err);
    });

    return response;
  } catch (error) {
    reportToBugsnag(error);
  }
}

export async function loadImage(url: string) {
  try {
    const response = await cacheImage(url);
    if (response) {
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
  } catch (error) {
    reportToBugsnag(error);
  }
}

function reportToBugsnag(error: unknown) {
  if (error instanceof Error) {
    Bugsnag.notify(error);
  } else {
    const wrapped = new Error("Unknown error");
    Bugsnag.notify(wrapped);
  }
}
